import { makeAutoObservable } from 'mobx';
import { createObservableTask, TaskModel } from '../models/task';
import { RawFile, RawTask } from '../interfaces/task';
import { genericUpsert, genericRemove, GenericUpsertOptions } from '../common';
import MobxTasksCacheService from '../../services/mobxTasksCacheService';
import { MY_TASKS_GROUP_FILTER_TYPE } from '../../global/constants';
import { ProjectModel } from '../models/project';
import { rootStore } from '../rootStore';
import { ProjectTemplateModel } from '../models/projectTemplate';
import { captureException } from '@sentry/react';

export class CreateTaskInput {
  id?: string;
  createdBy?: string;
  description?: string;
  groupId?: string;
  project?: ProjectModel | ProjectTemplateModel;
  name?: string;
  assigneesIds?: string[];
  status?: boolean;
  subscribersIds?: string[];
  priority?: number;
  dueDate?: Date | null;
  estimate?: number;
  milestoneId?: string | null;
  labelsIds?: string[];
  parentTaskId?: string;
  files: RawFile[] = [];
  statusId?: string;
  recurringInterval?: string | null;
  start?: string | null;
  cloneTaskId?: string;

  constructor() {
    makeAutoObservable(this);
  }

  get users() {
    return this.project?.users?.filter((user) => this.assigneesIds?.includes(user.id)) || [];
  }

  get subscribers() {
    return this.project?.users?.filter((user) => this.subscribersIds?.includes(user.id)) || [];
  }

  get labels() {
    return this.project?.labelsList?.filter((label) => this.labelsIds?.includes(label.id)) || [];
  }

  get milestone() {
    return this.project?.milestones?.find((milestone) => milestone.id === this.milestoneId) || null;
  }

  get taskGroup() {
    return this.project?.taskGroups?.find((group) => group.id === this.groupId) || null;
  }

  updateWith(task: TaskModel) {
    this.id = task.id;
    this.createdBy = task.createdBy;
    this.description = task.description;
    this.groupId = task.groupId ?? undefined;
    this.project = task.project ?? undefined;
    this.name = task.name;
    this.assigneesIds = task.users.map((user) => user.id);
    this.status = task.status;
    this.subscribersIds = task.taskSubscribers;
    this.priority = task.priority ?? undefined;
    this.dueDate = task.dueDate ?? undefined;
    this.estimate = task.estimate ?? undefined;
    this.milestoneId = task.milestoneId ?? undefined;
    this.labelsIds = task.labelsIds ?? undefined;
    this.parentTaskId = task.parentTaskId ?? undefined;
    this.files = task.files ?? [];
    this.statusId = task.statusId ?? undefined;
  }

  clear() {
    this.id = undefined;
    this.createdBy = undefined;
    this.description = undefined;
    this.groupId = undefined;
    this.project = undefined;
    this.name = undefined;
    this.assigneesIds = undefined;
    this.status = undefined;
    this.subscribersIds = undefined;
    this.priority = undefined;
    this.dueDate = undefined;
    this.estimate = undefined;
    this.milestoneId = undefined;
    this.labelsIds = undefined;
    this.parentTaskId = undefined;
    this.files = [];
    this.statusId = undefined;
    this.recurringInterval = undefined;
    this.start = undefined;
    this.cloneTaskId = undefined;
  }
  setId(id: string | undefined) {
    this.id = id;
  }
  setProject(projectId: string, isTemplateProject?: boolean) {
    this.project = isTemplateProject
      ? rootStore.projectTemplateStore.projectTemplates.find((project) => project.id === projectId)
      : rootStore.projectStore.projects.find((project) => project.id === projectId);
  }

  setParentTask(parentTaskId: string) {
    this.parentTaskId = parentTaskId;
  }

  setMilestone(milestoneId: string | null) {
    this.milestoneId = milestoneId;
  }

  setTaskGroup(groupId: string) {
    this.groupId = groupId;
  }

  setLabels(labelsIds: string[]) {
    this.labelsIds = labelsIds;
  }

  hasAssignee(assigneeId: string) {
    return this.assigneesIds?.includes(assigneeId);
  }

  addAssignee(assigneeId: string) {
    if (this.project?.multiAssignee) {
      this.assigneesIds = [...(this.assigneesIds || []), assigneeId];
    } else {
      this.assigneesIds = [assigneeId];
    }
  }

  removeAssignee(assigneeId: string) {
    this.assigneesIds = this.assigneesIds?.filter((id) => id !== assigneeId) || [];
  }

  clearAssignees() {
    this.assigneesIds = [];
  }

  setSubscribers(subscribersIds: string[]) {
    this.subscribersIds = subscribersIds;
  }

  setFiles(files: RawFile[]) {
    this.files = files;
  }

  addFiles(files: RawFile[]) {
    this.files.push(...files);
  }

  setPriority(priority: number) {
    this.priority = priority;
  }

  setDueDate(dueDate: Date | null) {
    this.dueDate = dueDate;
  }

  setEstimate(estimate: number) {
    this.estimate = estimate;
  }

  setName(name: string) {
    this.name = name;
  }

  hasLabel(labelId: string) {
    return this.labelsIds?.includes(labelId);
  }

  addLabel(labelId: string) {
    this.labelsIds = [...(this.labelsIds || []), labelId];
  }

  removeLabel(labelId: string) {
    this.labelsIds = this.labelsIds?.filter((id) => id !== labelId) || [];
  }

  setDescription(description: string) {
    this.description = description;
  }

  setStatus(status: boolean) {
    this.status = status;
  }

  setStatusId(statusId: string) {
    this.statusId = statusId;
  }

  setMilestoneId(milestoneId: string) {
    this.milestoneId = milestoneId;
  }

  setGroupId(groupId: string) {
    this.groupId = groupId;
  }

  setParentTaskId(parentTaskId: string) {
    this.parentTaskId = parentTaskId;
  }

  setRecurringInterval(recurringInterval: string | null) {
    this.recurringInterval = recurringInterval;
  }

  setStart(start: string | null) {
    this.start = start;
  }

  setCloneTaskId(cloneTaskId: string | undefined) {
    this.cloneTaskId = cloneTaskId;
  }
}

export class TaskStore {
  tasks: TaskModel[] = [];
  currentTask: TaskModel | null = null;
  // track which task's assign popup is open on the page
  currentAssignBoxTaskId: string = '';
  myTasksFilter: number = MY_TASKS_GROUP_FILTER_TYPE.DUE_DATE;
  createTaskInput: CreateTaskInput = new CreateTaskInput();
  recurringTasks: TaskModel[] = [];
  isTaskFilterOpen?: boolean = false;
  loading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  // Lookup indexes (computed) so per-task getters like subTasks/project resolve in O(1)
  // instead of each consumer re-scanning the whole tasks array on every access.
  get tasksById() {
    const map = new Map<string, TaskModel>();
    this.tasks.forEach((task) => map.set(task.id, task));
    return map;
  }

  get tasksByParentId() {
    const map = new Map<string, TaskModel[]>();
    this.tasks.forEach((task) => {
      if (!task.parentTaskId) return;
      const siblings = map.get(task.parentTaskId);
      if (siblings) siblings.push(task);
      else map.set(task.parentTaskId, [task]);
    });
    return map;
  }

  get tasksByProjectId() {
    const map = new Map<string, TaskModel[]>();
    this.tasks.forEach((task) => {
      const projectTasks = map.get(task.projectId);
      if (projectTasks) projectTasks.push(task);
      else map.set(task.projectId, [task]);
    });
    return map;
  }

  setCurrentAssignBoxTaskId(taskId: string) {
    this.currentAssignBoxTaskId = taskId;
  }

  clearCurrentAssignBoxTaskId() {
    this.currentAssignBoxTaskId = '';
  }

  // Resolves the task a late async response belongs to, so task-scoped writes land on the task
  // they were requested for rather than whichever task is open when the response arrives.
  getTaskById(taskId: string | undefined | null) {
    if (!taskId) return null;
    // Prefer the open task: `tasks` is rebuilt with fresh instances without reassigning
    // `currentTask`, and the detail page renders `currentTask`, so writes must hit that instance.
    if (this.currentTask?.id === taskId) return this.currentTask;
    return this.tasksById.get(taskId) ?? null;
  }

  setCurrentTask(taskId: string) {
    this.currentTask = this.tasks.find((task) => task.id === taskId) || null;
  }

  clearCurrentTask() {
    this.currentTask = null;
  }

  setTasks(rawTasks: RawTask[]) {
    MobxTasksCacheService.getInstance().setTasks(rawTasks);
    this.tasks = rawTasks.map((rawTask) => createObservableTask(rawTask));
  }

  setRecurringTasks(rawTasks: RawTask[]) {
    this.recurringTasks = rawTasks.map((rawTask) => createObservableTask(rawTask));
  }

  removeRecurringTasks(taskIds: string[]) {
    this.recurringTasks = this.recurringTasks.filter((task) => !taskIds.includes(task.id));
  }

  addTasks(rawTasks: RawTask[]) {
    const tasks = rawTasks.map((rawTask) => createObservableTask(rawTask));
    this.tasks.push(...tasks);
    MobxTasksCacheService.getInstance().setTasks(rawTasks);
  }

  upsertTasks(rawTasks: RawTask[], { excludeFields, currentId }: GenericUpsertOptions<TaskModel> = {}) {
    // Merge into existing instances rather than replacing them: memoized task rows keep
    // rendering the instance they were mounted with, so realtime updates (assignees,
    // labels, status, ...) must land on that same instance to be picked up.
    const { newItems, updatedItems, updatedCurrentItem } = genericUpsert(this.tasks, rawTasks, createObservableTask, {
      excludeFields,
      currentId: currentId ?? this.currentTask?.id,
      strategy: 'merge'
    });

    if (updatedCurrentItem) {
      this.currentTask = updatedCurrentItem;
    }

    MobxTasksCacheService.getInstance().setTasks(rawTasks);
    return { newItems, updatedItems };
  }

  removeTasks(taskIds: string[]) {
    this.tasks = genericRemove(this.tasks, taskIds);
    MobxTasksCacheService.getInstance().removeBulk(taskIds);
  }

  syncList(newRawTasks: RawTask[], archivedTasks: RawTask[], { excludeFields }: GenericUpsertOptions<TaskModel> = {}) {
    if (archivedTasks.length > 0) {
      this.removeTasks(archivedTasks.map((task) => task.id));
    }

    if (newRawTasks.length > 0) {
      this.upsertTasks(newRawTasks, { excludeFields });
    }
  }

  setMyTasksFilter(filter: number) {
    this.myTasksFilter = filter;
  }

  setIsTaskFilterOpen(isOpen: boolean) {
    this.isTaskFilterOpen = isOpen;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  *updateWithIndexdb() {
    try {
      const rawTasks: RawTask[] = yield MobxTasksCacheService.getInstance().getTasks();
      this.tasks = rawTasks.map((rawTask) => createObservableTask(rawTask));
    } catch (err) {
      captureException(err);
      // Do not throw — allow app to continue and wait for Firebase sync
    }
  }

  // Clear task related data
  clearTaskData() {
    this.tasks = [];
    this.currentTask = null;
    this.recurringTasks = [];
    this.createTaskInput = new CreateTaskInput();
    this.loading = false;
  }
}

export const taskStore = new TaskStore();
